import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type {
  ClickUIElementInputProperties,
  IEngagementTeaser,
  IEngagementUIElement,
  IEngagementWidget,
  IImpressionTeaser,
  IImpressionUIElement,
  IImpressionWidget,
  IViewArticle,
  IViewError,
  IViewFrontpage,
  IViewListing,
  IViewLockedArticle,
  IViewPage,
  Tracker,
  ViewArticleInputProperties,
  ViewListingInputProperties,
  ViewPageInputProperties,
  ViewSalesPosterInputProperties,
} from '@schibsted/pulse-sdk';
import type { PulseExperiment } from '../../../public-src/core/js/pulse/pulse-util.js';
import type {
  ByfunnPulseContextInputParams,
  ByfunnPulseContextProviderProps,
  ByfunnPulseContextValue,
} from './types.js';

import { getByfunnPulseTracker } from '../../../public-src/byfunn/pulse/pulse-tracker.js';
import {
  createPulseObject,
  createPulseTarget,
  getPulseMeta,
} from '../../../public-src/core/js/pulse/pulse-util.js';
import { getTeaserMetrics } from '../../core/mandatory-integrations/pulse/utils/metrics.js';
import { defaultByfunnPulseConfig } from './config.js';
import { buildEngagementClickEvent } from './event/event-builders/build-engagement-click-event.js';
import { buildImpressionEvent } from './event/event-builders/build-impression-event.js';
import { buildProviderInput } from './event/event-builders/build-provider-input.js';
import { buildViewEvent } from './event/event-builders/build-view-event.js';
import { provideFakePulseAutotracker } from './pulse-autotracker-fake.server.js';

const ByfunnPulseContext = createContext<ByfunnPulseContextValue | null>(null);

const ByfunnPulseContextProvider: React.FC<ByfunnPulseContextProviderProps> = ({
  config,
  children,
  experiments = [],
  isTpaasEnabled,
}) => {
  const [pulseTracker, setPulseTracker] = useState<Tracker | null>(null);

  const { providerId, productTag, targetNewsroom } = config;

  const providerInput = buildProviderInput(config);

  useEffect(() => {
    if (!pulseTracker) {
      getByfunnPulseTracker(providerId, productTag).then((tracker) => {
        setPulseTracker(tracker);

        provideFakePulseAutotracker(tracker);

        const baseExperiments = window.PULSE_EXPERIMENTS || [];

        const isFrontpage = () => window.location.pathname === '/';
        if (isFrontpage()) {
          const { experiment } = window.CURATE_CONFIG || {};
          if (experiment) {
            baseExperiments.push(experiment);
          }
        }
        // Add experiments right away since we can't know if view frontpage
        // fires before the other useEffect for updating experiments
        tracker.update({
          experiments: baseExperiments.concat(experiments),
        });
      });
    }
  }, [pulseTracker, providerId, productTag, experiments]);

  const registerPageLeaveTracking = useCallback(
    ({
      objectElement,
      pageElement = document.querySelector('body') ?? undefined,
    }: {
      objectElement: Element;
      pageElement?: Element;
    }) => {
      if (pulseTracker && pageElement) {
        pulseTracker.addPageLeaveTracking(objectElement, pageElement);
      }
    },
    [pulseTracker],
  );

  const getEnvironmentId = useCallback(() => {
    if (pulseTracker) {
      return pulseTracker.getEnvironmentId();
    }

    return Promise.resolve(null);
  }, [pulseTracker]);

  const registerViewElementTracking = useCallback(() => {
    if (!pulseTracker) {
      return;
    }

    pulseTracker.addElementVisibilityObserver(
      '[data-pulse-entity-id]',
      (element) => {
        const pulseMeta = getPulseMeta(element);
        const target = createPulseTarget(pulseMeta, providerId, targetNewsroom);
        const object = createPulseObject(pulseMeta);

        const { variant } = window.CURATE_CONFIG || {};

        const metrics = getTeaserMetrics(variant, pulseMeta);
        const teaserExperiments = [...(pulseMeta.experiments || [])];

        pulseTracker.trackLegacyViewUIElement({
          object,
          target,
          metrics,
          experiments: teaserExperiments,
        });

        const objectType = element.dataset.trackObjectType;
        const tpaas = element.dataset.trackEvent;

        if (!isTpaasEnabled || !tpaas) {
          return;
        }

        if (objectType === 'Teaser') {
          pulseTracker.trackImpressionTeaser(
            buildImpressionEvent<IImpressionTeaser>({
              elementType: 'Teaser',
              event: tpaas,
              providerId,
              providerInput,
            }),
          );
        } else if (objectType === 'Widget') {
          pulseTracker.trackImpressionWidget(
            buildImpressionEvent<IImpressionWidget>({
              elementType: 'Widget',
              event: tpaas,
              providerId,
              providerInput,
            }),
          );
        } else if (objectType === 'UIElement') {
          pulseTracker.trackImpressionUIElement(
            buildImpressionEvent<IImpressionUIElement>({
              elementType: 'UIElement',
              event: tpaas,
              providerId,
              providerInput,
            }),
          );
        }
      },
    );
  }, [pulseTracker, providerId, targetNewsroom, providerInput, isTpaasEnabled]);

  const trackClickTeaser = useCallback(
    (
      legacy: ClickUIElementInputProperties,
      tpaas: ByfunnPulseContextInputParams<IEngagementTeaser>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyClickUIElement(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackEngagementTeaser(
        buildEngagementClickEvent<IEngagementTeaser>({
          elementType: 'Teaser',
          event: tpaas,
          providerId,
          providerInput,
        }),
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackClickUIElement = useCallback(
    (
      legacy: ClickUIElementInputProperties,
      tpaas: ByfunnPulseContextInputParams<IEngagementUIElement>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyClickUIElement(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackEngagementUIElement(
        buildEngagementClickEvent<IEngagementUIElement>({
          elementType: 'UIElement',
          event: tpaas,
          providerId,
          providerInput,
        }),
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackClickWidget = useCallback(
    (
      legacy: ClickUIElementInputProperties,
      tpaas: ByfunnPulseContextInputParams<IEngagementWidget>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyClickUIElement(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackEngagementWidget(
        buildEngagementClickEvent<IEngagementWidget>({
          elementType: 'Widget',
          event: tpaas,
          providerId,
          providerInput,
        }),
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewArticle = useCallback(
    (
      legacy: ViewArticleInputProperties,
      tpaas: ByfunnPulseContextInputParams<IViewArticle>,
      objectElement: Element,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewArticle(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewArticle(
        buildViewEvent<IViewArticle>({
          pageType: 'Article',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: { objectElement } },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  // Note: We fire Listing for legacy, but Frontpage for TPaaS
  const trackViewFrontpage = useCallback(
    (
      legacy: ViewListingInputProperties,
      tpaas: ByfunnPulseContextInputParams<IViewFrontpage>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewListing(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewFrontpage(
        buildViewEvent<IViewFrontpage>({
          pageType: 'Frontpage',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: false },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewListing = useCallback(
    (
      legacy: ViewListingInputProperties,
      tpaas: ByfunnPulseContextInputParams<IViewListing>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewListing(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewListing(
        buildViewEvent<IViewListing>({
          pageType: 'Listing',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: false },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewPage = useCallback(
    (
      legacy: ViewPageInputProperties,
      tpaas: ByfunnPulseContextInputParams<IViewPage>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewPage(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewPage(
        buildViewEvent<IViewPage>({
          pageType: 'Page',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: false },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewError = useCallback(
    (
      legacy: ViewPageInputProperties,
      tpaas: ByfunnPulseContextInputParams<IViewError>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewPage(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewError(
        buildViewEvent<IViewError>({
          pageType: 'Error',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: false },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewLockedArticle = useCallback(
    (
      legacy: ViewSalesPosterInputProperties,
      tpaas: ByfunnPulseContextInputParams<IViewLockedArticle>,
      objectElement: Element,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewSalesPoster(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewLockedArticle(
        buildViewEvent<IViewLockedArticle>({
          pageType: 'LockedArticle',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: { objectElement } },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  return (
    <ByfunnPulseContext.Provider
      value={{
        registerViewElementTracking,
        registerPageLeaveTracking,
        getEnvironmentId,
        trackClickTeaser,
        trackClickUIElement,
        trackClickWidget,
        trackViewArticle,
        trackViewFrontpage,
        trackViewListing,
        trackViewPage,
        trackViewError,
        trackViewLockedArticle,
      }}
    >
      {children}
    </ByfunnPulseContext.Provider>
  );
};

const useByfunnPulseContext = (): ByfunnPulseContextValue => {
  const context = useContext(ByfunnPulseContext);

  if (!context) {
    throw new Error('No Byfunn Pulse context provided');
  }

  return context;
};

type WithByfunnPulseProps = {
  experiments?: PulseExperiment[];
  isTpaasEnabled?: boolean;
};

const withByfunnPulse = <P extends Record<string, unknown>>(
  Component: React.FC<P>,
  pulseConfig = defaultByfunnPulseConfig,
): React.FC<Omit<P, keyof WithByfunnPulseProps> & WithByfunnPulseProps> => {
  const Hoc = (
    props: Omit<P, keyof WithByfunnPulseProps> & WithByfunnPulseProps,
  ) => {
    const { experiments, isTpaasEnabled = false, ...rest } = props;

    return (
      <ByfunnPulseContextProvider
        config={pulseConfig}
        experiments={experiments}
        isTpaasEnabled={isTpaasEnabled}
      >
        <Component {...(rest as P)} />
      </ByfunnPulseContextProvider>
    );
  };

  Hoc.displayName = `withByfunnPulse<${Component.displayName}>`;

  return Hoc;
};

export { useByfunnPulseContext, withByfunnPulse };
